import { format, parseISO, eachDayOfInterval, addDays } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import type { Activity } from '@/types'
import { formatInTimezone, getLocalDate, getTimezoneAbbr } from '@/lib/timezones'
import { titleWithEmoji } from '@/lib/activityEmoji'
import { cn } from '@/lib/utils'
import { MapPin, Link as LinkIcon } from 'lucide-react'
import { useNow } from '@/hooks/useNow'
import { getActivityStatusMap } from '@/lib/activityStatus'
import type { ActivityStatus } from '@/lib/activityStatus'
import { ActivityDetailDrawer } from './ActivityDetailDrawer'

interface Props {
  activities: Activity[]
  weekStart: string // YYYY-MM-DD of Monday
  // Activity ids to render faded — artist "show all" informational activities.
  dimmedIds?: Set<string>
}

export function WeekView({ activities, weekStart, dimmedIds }: Props) {
  const navigate = useNavigate()
  const now = useNow()
  const statusMap = getActivityStatusMap(activities, now)
  const today = format(new Date(), 'yyyy-MM-dd')

  const days = eachDayOfInterval({
    start: parseISO(weekStart),
    end: addDays(parseISO(weekStart), 6),
  }).map(d => format(d, 'yyyy-MM-dd'))

  // Group activities by venue-local date
  const byDay = activities.reduce<Record<string, Activity[]>>((acc, a) => {
    const d = getLocalDate(a.start_time_utc, a.venue_timezone)
    if (!acc[d]) acc[d] = []
    acc[d].push(a)
    return acc
  }, {})

  return (
    <div className="grid grid-cols-7 gap-2 lg:gap-3">
      {days.map(date => {
        const dayActivities = byDay[date] ?? []
        const isToday = date === today
        const dateObj = parseISO(date)

        return (
          <div key={date} className="flex flex-col min-h-[400px] lg:min-h-[540px]">
            {/* Day header */}
            <button
              onClick={() => navigate(`/day/${date}`)}
              className={cn(
                'text-center py-2 lg:py-3 border-b-2 transition-colors',
                isToday
                  ? 'border-primary text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground'
              )}
            >
              <p className="text-[10px] lg:text-[11px] uppercase tracking-wider font-medium">
                {format(dateObj, 'EEE')}
              </p>
              <p className={cn(
                'text-lg lg:text-2xl font-semibold tabular-nums leading-tight',
                isToday && 'text-primary'
              )}>
                {format(dateObj, 'd')}
              </p>
            </button>

            {/* Activities */}
            <div className="flex-1 py-2 lg:py-3 space-y-1.5 lg:space-y-2">
              {dayActivities.length === 0 ? (
                <p className="text-[10px] lg:text-xs text-muted-foreground/50 text-center pt-2 italic">
                  —
                </p>
              ) : (
                dayActivities.map(activity => (
                  <div
                    key={activity.id}
                    style={{ viewTransitionName: `activity-${activity.id}` }}
                    className={cn(
                      'transition-opacity dur-base ease-out-soft',
                      dimmedIds?.has(activity.id) && 'opacity-50 hover:opacity-80'
                    )}
                  >
                    <MiniActivityCard
                      activity={activity}
                      status={statusMap[activity.id]}
                      now={now}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MiniActivityCard({
  activity,
  status,
  now,
}: {
  activity: Activity
  status?: ActivityStatus
  now: Date
}) {
  const time = formatInTimezone(activity.start_time_utc, activity.venue_timezone)
  const tz = getTimezoneAbbr(activity.venue_timezone)
  const hasLinks = (activity.activity_links?.length ?? 0) > 0
  const isLive = status === 'live'
  const isPast = status === 'past'

  return (
    <ActivityDetailDrawer activity={activity} status={status} now={now}>
      <div
        className={cn(
          'w-full text-left rounded-md lg:rounded-xl border transition-all dur-fast px-2 py-1.5 lg:px-3 lg:py-2.5 space-y-0.5 lg:space-y-1.5 cursor-pointer lg:hover:-translate-y-[1px] active:scale-[0.99]',
          isLive
            ? 'border-primary/50 bg-primary/5 hover:bg-primary/10 lg:shadow-[0_3px_12px_-6px_color-mix(in_oklab,var(--primary)_40%,transparent)]'
            : 'border-border bg-card hover:bg-accent/50 hover:border-border-strong',
          isPast && 'opacity-55',
        )}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="mono text-[10px] lg:text-[11px] font-semibold text-foreground tabular-nums">
            {time}
          </span>
          <span className="mono text-[9px] lg:text-[10px] text-muted-foreground opacity-70 tracking-wider">{tz}</span>
        </div>
        <p className="text-xs lg:text-[13px] font-medium lg:font-semibold text-foreground leading-tight lg:leading-snug lg:tracking-[-0.005em] line-clamp-2 lg:line-clamp-3">
          {titleWithEmoji(activity.titulo)}
        </p>
        {activity.venue_name && (
          <div className="flex items-center gap-0.5 lg:gap-1 text-[10px] lg:text-[11px] text-muted-foreground">
            <MapPin className="w-2.5 h-2.5 lg:w-3 lg:h-3 shrink-0" strokeWidth={2} />
            <span className="truncate">{activity.venue_name}</span>
          </div>
        )}
        {hasLinks && (
          <div className="flex items-center gap-0.5 lg:gap-1 text-[10px] lg:text-[11px] text-muted-foreground">
            <LinkIcon className="w-2.5 h-2.5 lg:w-3 lg:h-3" strokeWidth={2} />
            <span className="tabular-nums">{activity.activity_links!.length}</span>
          </div>
        )}
      </div>
    </ActivityDetailDrawer>
  )
}
